import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Shield, LogIn, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { adminApi } from "../lib/api";

/**
 * Dedicated Master Admin login. Reuses the existing `/api/auth/login`
 * password endpoint — no separate password store, no plaintext credentials
 * anywhere. After sign-in we call `/api/admin/whoami` to confirm this
 * account is authorized for the Admin area. A regular LifeGuide user who
 * successfully signs in here still receives a clear Access Denied and NO
 * admin cookies/tokens beyond the normal user session.
 */
export default function AdminLoginPage() {
  const { user, login } = useAuth();
  const location = useLocation();
  const nextPath = new URLSearchParams(location.search).get("next") || "/admin";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("form"); // form | checking | denied
  const [deniedEmail, setDeniedEmail] = useState("");

  // If already signed in as an admin, jump straight to /admin.
  useEffect(() => {
    if (user) {
      setPhase("checking");
      adminApi
        .whoami()
        .then((r) => {
          if (r?.is_master_admin) {
            window.location.replace(nextPath);
          } else {
            setDeniedEmail(user.email || "");
            setPhase("denied");
          }
        })
        .catch(() => setPhase("form"));
    }
  }, [user, nextPath]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await login(email.trim(), password);
      // Now verify admin authorization via whoami (never trust the frontend).
      const who = await adminApi.whoami();
      if (who?.is_master_admin) {
        window.location.replace(nextPath);
        return;
      }
      setDeniedEmail(email.trim());
      setPhase("denied");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Sign-in failed — check your email and password."
      );
    } finally {
      setBusy(false);
    }
  };

  if (phase === "checking") {
    return (
      <div className="min-h-screen bg-[#0B1210] flex items-center justify-center text-[#B7D2AF]">
        <Loader2 size={18} className="animate-spin mr-2" /> Verifying admin access…
      </div>
    );
  }

  if (phase === "denied") {
    return (
      <div className="min-h-screen bg-[#0B1210] flex items-center justify-center px-6">
        <div
          data-testid="admin-login-denied"
          className="max-w-md w-full rounded-3xl border border-[#7C3E33]/50 bg-[#1A100E] p-8 text-center"
        >
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#E38680] mb-3">
            Access Denied
          </div>
          <h1 className="font-display text-3xl text-white mb-2">
            Not an admin account
          </h1>
          <p className="text-sm text-[#B7D2AF] leading-relaxed mb-6">
            Signed in as <span className="text-white">{deniedEmail}</span>. This
            account is not authorized for the Master Admin area.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              data-testid="admin-login-denied-retry"
              onClick={() => {
                try {
                  localStorage.removeItem("lg_token");
                } catch {}
                window.location.reload();
              }}
              className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-6 py-2.5 text-sm font-medium"
            >
              Sign in with a different account
            </button>
            <a
              href="/"
              className="text-sm text-[#B7D2AF] hover:text-white"
            >
              Back to LifeGuide
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1210] text-white flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-[#B7D2AF] hover:text-white text-sm mb-6">
          ← Back to LifeGuide
        </Link>
        <div
          data-testid="admin-login-page"
          className="rounded-3xl border border-white/10 bg-[#141B18] p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-2xl bg-[#1F2A24] border border-[#4F6F52]/50 flex items-center justify-center">
              <Shield size={16} className="text-[#8FBF87]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#B7D2AF]">
                Master Admin
              </div>
              <div className="font-display text-xl">Sign in</div>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4" noValidate>
            <div>
              <label className="block text-xs text-[#8FA894] mb-1.5 uppercase tracking-[0.14em]">
                Email
              </label>
              <input
                data-testid="admin-login-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-[#0B1210] border border-white/10 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/30"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-xs text-[#8FA894] mb-1.5 uppercase tracking-[0.14em]">
                Password
              </label>
              <input
                data-testid="admin-login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full bg-[#0B1210] border border-white/10 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/30"
              />
            </div>
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={busy || !email || !password}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full py-3 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Verifying…
                </>
              ) : (
                <>
                  <LogIn size={16} /> Sign in
                </>
              )}
            </button>
          </form>
          <p className="text-[11px] text-[#7C8F7C] mt-5 leading-relaxed">
            Admin access is only granted to accounts whose email is present in
            the platform's <code>MASTER_ADMIN_EMAILS</code> secret or whose
            user record has been promoted to <code>role: owner</code>. Password
            handling is delegated to the existing LifeGuide authentication
            system — no separate credential store.
          </p>
        </div>
      </div>
    </div>
  );
}
